import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Card from "components/card";
import Button from "components/button";
import { getEvent } from "services/events";
import { toggleAttendance } from "services/events";

const AdminEventAttendance: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const { data } = await getEvent(id);
        setEvent(data);
      } catch (err) {
        console.error(err);
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };
    load();
    // Poll for updates every 8 seconds
    const iv = setInterval(() => {
      load();
    }, 8000);

    return () => clearInterval(iv);
  }, [id]);

  const handleToggle = async (rsvpId: number) => {
    if (!id) return;
    setToggling(rsvpId);
    try {
      await toggleAttendance(id, rsvpId);
      // refresh event data
      const { data } = await getEvent(id);
      setEvent(data);
    } catch (err) {
      console.error(err);
    } finally {
      setToggling(null);
    }
  };

  if (loading)
    return (
      <Card extra="w-full p-4">
        <div>Loading...</div>
      </Card>
    );

  if (!event)
    return (
      <Card extra="w-full p-4">
        <div className="text-gray-500">Event not found.</div>
      </Card>
    );

  const stats = {
    total: event.rsvps?.length ?? 0,
    going: event.rsvps?.filter((r: any) => r.status === "going").length ?? 0,
    maybe: event.rsvps?.filter((r: any) => r.status === "maybe").length ?? 0,
    not_going:
      event.rsvps?.filter((r: any) => r.status === "not_going").length ?? 0,
    attended: event.rsvps?.filter((r: any) => r.attended).length ?? 0,
  };

  return (
    <Card extra="w-full p-4">
      <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold">Attendance — {event.name}</h2>
          <div className="mt-1 text-sm text-gray-500">
            {event.event_date} {event.event_time}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-4 text-sm text-gray-600 md:mt-0">
          <div>
            Total RSVPs:{" "}
            <strong className="ml-1 text-gray-900">{stats.total}</strong>
          </div>
          <div className="text-green-600">
            Going: <strong className="ml-1 text-gray-900">{stats.going}</strong>
          </div>
          <div className="text-yellow-600">
            Maybe: <strong className="ml-1 text-gray-900">{stats.maybe}</strong>
          </div>
          <div className="text-red-600">
            Not going:{" "}
            <strong className="ml-1 text-gray-900">{stats.not_going}</strong>
          </div>
          <div>
            Attended:{" "}
            <strong className="ml-1 text-gray-900">{stats.attended}</strong>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {event.rsvps && event.rsvps.length > 0 ? (
          event.rsvps.map((rsvp: any) => (
            <div
              key={rsvp.id}
              className="flex flex-col rounded-lg bg-white p-3 shadow-sm dark:bg-navy-700 md:flex-row md:items-center md:justify-between"
            >
              <div>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-semibold">
                    {rsvp.user?.fullName ||
                      rsvp.user?.firstname + " " + rsvp.user?.lastname}
                  </div>
                  <div className="text-xs text-gray-400">
                    {rsvp.user?.email}
                  </div>
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  {rsvp.user?.ageGrade?.name}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 md:mt-0">
                <div
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    rsvp.status === "going"
                      ? "bg-green-100 text-green-800"
                      : rsvp.status === "maybe"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {rsvp.status}
                </div>
                <div className="text-sm text-gray-500">{rsvp.created_at}</div>
                <Button
                  size="sm"
                  variant={rsvp.attended ? "danger" : "neutral"}
                  onClick={() => handleToggle(rsvp.id)}
                  disabled={toggling === rsvp.id}
                >
                  {toggling === rsvp.id
                    ? "..."
                    : rsvp.attended
                    ? "Mark Not Attended"
                    : "Mark Attended"}
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-gray-500">No RSVPs yet for this event.</div>
        )}
      </div>
    </Card>
  );
};

export default AdminEventAttendance;
